import {
  MilestoneEntity,
  OrderDependencyEntity,
  OrderEntity,
  PauseEntity,
  TradeEntity,
} from '@/common/types/entities';
import { Collision } from '@/features/collisions/types';
import { SectionsMap } from '@/features/projectStructure/utils/sectionTreeUtils';
import { DinPaperFormat, PageOrientation } from '@/features/scheduleExport/pdfExport/types';
import { DateRangeObject } from '@/helpers/utils/dates';
import { SchedulerEventColorType } from '@/services/store/schedule/types/types';

import { AbsoluteScheduleRange } from '../scheduleViews/types';

export enum ExportTab {
  EXCEL = 'excel',
  GANTT = 'gantt',
  PDF = 'pdf',
}

export enum ExportKey {
  EXCEL = 'excel',
  MS_PROJECT = 'ms_project',
  POWER_PROJECT = 'powerproject',
  PRIMAVERA_P6 = 'p6',
  PDF = 'pdf',
}

export enum GanttExportKey {
  MS_PROJECT = 'msproject',
  POWER_PROJECT = 'powerproject',
  PRIMAVERA_P6 = 'primaverap6',
}

export function mapGanttExportKeyToExportKey(key: GanttExportKey): ExportKey {
  switch (key) {
    case GanttExportKey.MS_PROJECT:
      return ExportKey.MS_PROJECT;
    case GanttExportKey.POWER_PROJECT:
      return ExportKey.POWER_PROJECT;
    case GanttExportKey.PRIMAVERA_P6:
      return ExportKey.PRIMAVERA_P6;
  }
}

/** Desired export type (form of exported pdf pages) */
export enum PdfExportType {
  DIGITAL = 'digital',
  PRINT = 'print',
  PLOT = 'plot',
}

export interface PdfExportFormat {
  type: PdfExportType;
  orientation: PageOrientation;
  paperFormat: DinPaperFormat | string;
  isMultiPage: boolean;
}

export type PdfCustomExportConfig = PdfExportFormat & {
  currentZoomPreset: string;
  projectName: string | undefined;
  tenantLogo: string | null | undefined;
  exportRange: DateRangeObject;
  precalculatedMaxLanesPerResource: Map<string, number>;
};

export function getInitialPdfExportFormat(): PdfExportFormat {
  return {
    type: PdfExportType.PRINT,
    orientation: PageOrientation.LANDSCAPE,
    paperFormat: DinPaperFormat.A4,
    isMultiPage: false,
  };
}

export type ExportDateRange = {
  start: string;
  end: string;
} | null;

export interface ExportSettingsInput {
  advancedSettings: {
    customPdfHeadline: string;
    groupSequences: boolean;
    hideDependencies: boolean;
    hideWeekends: boolean;
    showCollisions: boolean;
    showTodayLine: boolean;
  };
  collapsedRowIds: Set<string>;
  contributorGroups: string[];
  eventColorType: SchedulerEventColorType;
  logoSrc: string | null;
  printerSettings: {
    type: PdfExportType;
    orientation: PageOrientation;
    paperFormat: DinPaperFormat;
    printOnMultiplePages: boolean;
  };
  sections: string[];
  totalSections: number;
  tenantTradeVariations: string[];
  timeRange: AbsoluteScheduleRange | null;
}

export interface ExportOptions {
  contributorGroups?: Array<string>;
  groupByTradeSequenceInstances?: boolean;
  timeRange?: { start: string; end: string };
  trades?: Array<string>;
  type: ExportKey | null;
  wbsSections?: string[];
}

export interface ExportCallbacks {
  onExportStart?: () => void;
  onExportSuccess?: () => void;
  onExportError?: () => void;
}

export type ExportEntities = {
  collisions: Map<string, Collision>;
  dependencies: Map<string, OrderDependencyEntity>;
  milestones: Map<string, MilestoneEntity>;
  orders: Map<string, OrderEntity>;
  pauses: Map<string, PauseEntity>;
  trades: Map<string, TradeEntity>;
  wbsSections: SectionsMap;
};

export type ExportStatus = 'WAITING' | 'IN_PROGRESS' | 'SUCCESS' | 'FAILED';
